/**
 * What kind of step a walkthrough step is (spec 53 section 4.3), from the
 * fields it already has. Nothing in a definition says "graded": the outcome
 * a step waits on decides it.
 *
 *   read   no `act`, no `until`, no `watch`   (Next)
 *   watch  `watch` or `drive`, nothing to do  (the event arrives)
 *   find   `until.element`                    something appears
 *   enter  `until.field`                      the person fills a control
 *   save   `until.request`                    the server answered
 *
 * A step that waits on a request AND an element (Save, then the dialog it
 * opens) is a save: the write is what has to be the person's own.
 *
 * `drive` (spec 53 5.5) is the practice fleet's simulated driver: the card
 * offers "Send the driver" and waits for the delivery (TourHost sendDriver).
 * Nothing the person does there is graded; what they do with the delivery is.
 */
import { flattenSteps } from '../tours/engine';

export const GRADED_KINDS = Object.freeze(['find', 'enter', 'save']);

export function kindOf(step) {
  const until = (step && step.until) || {};
  if (until.request) return 'save';
  if (until.field) return 'enter';
  if (until.element) return 'find';
  if (step && (step.watch || step.drive) && !Array.isArray(step.act)) return 'watch';
  return 'read';
}

export function isGraded(step) {
  return GRADED_KINDS.includes(kindOf(step));
}

/**
 * The graded steps of a definition, as the module registry
 * (api/app/Services/Training/modules.js) lists them: ids by kind, in the
 * definition's order. Every step counts, whatever role reads it: a module's
 * pass rule does not change with who takes it.
 *
 * A step and its `-any` stand-in (shown instead to a fleet with none of that
 * kind) are one entry, a group of which either counts, at the named step's
 * place: a run only ever meets one of the two (grade.js).
 */
export function gradedOf(definition) {
  const graded = { find: [], enter: [], save: [] };
  for (const step of flattenSteps(definition, null)) {
    const kind = kindOf(step);
    if (graded[kind]) graded[kind].push(step.id);
  }
  for (const kind of Object.keys(graded)) {
    const ids = graded[kind];
    graded[kind] = ids
      .filter(id => !(id.endsWith('-any') && ids.includes(id.slice(0, -4))))
      .map(id => (ids.includes(`${id}-any`) ? [id, `${id}-any`] : id));
  }
  return graded;
}

/** The registry entry the grader needs, made from a ui definition. */
export function moduleOf(definition) {
  const training = (definition && definition.training) || {};
  return {
    id: training.module || (definition && definition.id),
    version: definition && definition.version,
    course: training.course || null,
    chapter: Number.isInteger(training.chapter) ? training.chapter : null,
    stage: training.stage || null,
    graded: gradedOf(definition),
  };
}
